import { CircleCheck, Phone, Mail, Cake, AlertCircle, PartyPopper } from "lucide-react";
import { SITE, IMAGES } from "../data/site";
import { Reveal, CallButton, EmailButton, Confetti, SectionHeading } from "../components/Shared";

const steps = [
  {
    title: "Contact Ms. Jeanette",
    text: (
      <>
        Call <a href={SITE.phoneHref} data-testid="step-call-link" className="font-extrabold text-blue-600 underline focus:outline-none focus:ring-4 focus:ring-blue-300 rounded">{SITE.phone}</a>{" "}
        or email <a href={SITE.emailHref} data-testid="step-email-link" className="break-all font-extrabold text-blue-600 underline focus:outline-none focus:ring-4 focus:ring-blue-300 rounded">{SITE.email}</a>
      </>
    ),
  },
  {
    title: "Discuss Your Date",
    text: "Ms. Jeanette will help confirm availability and party details.",
  },
  {
    title: "Reserve Your Party",
    text: "Pay the $50 nonrefundable booking deposit directly through the owner according to her booking process.",
  },
];

export default function Birthday() {
  return (
    <>
      <section className="relative overflow-hidden bg-yellow-50 px-4 pb-20 pt-14 sm:px-6" data-testid="birthday-hero">
        <Confetti />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <span className="mb-5 inline-block rounded-full bg-white px-4 py-1.5 text-xs font-extrabold uppercase tracking-widest text-red-600 shadow-sm">
              Children's birthday parties
            </span>
            <h1 className="text-4xl font-semibold sm:text-5xl lg:text-6xl">Make Their Birthday Extra Sweet!</h1>
            <p className="mt-5 text-base font-extrabold text-slate-800 md:text-lg">
              A birthday celebration filled with snowballs, imagination, pictures, and memories.
            </p>
            <p className="mt-3 text-sm text-slate-600 sm:text-base">
              Birthday party bookings are handled directly through owner{" "}
              <strong className="text-slate-800">{SITE.owner}</strong> — no online forms, just a friendly
              conversation.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <CallButton testid="birthday-hero-book-btn">Book With Ms. Jeanette</CallButton>
              <EmailButton testid="birthday-hero-email-btn" />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <img src={IMAGES.birthday.url} alt={IMAGES.birthday.alt} className="aspect-[4/3] w-full rounded-3xl object-cover shadow-2xl shadow-yellow-500/25" data-testid="birthday-hero-image" />
          </Reveal>
        </div>
      </section>

      <section className="px-4 py-20 sm:px-6" data-testid="birthday-package-section">
        <SectionHeading eyebrow="The party package" title="Ms. Jeanette's Birthday Party" testid="birthday-package-heading" />
        <Reveal className="mx-auto max-w-2xl">
          <div className="rounded-3xl border-4 border-dashed border-yellow-400 bg-yellow-50 p-8 text-center shadow-2xl shadow-yellow-500/15 sm:p-12" data-testid="birthday-package-card">
            <span className="inline-flex items-center gap-2 rounded-full bg-red-500 px-5 py-2 text-sm font-extrabold uppercase tracking-widest text-white">
              <Cake className="h-4 w-4" aria-hidden="true" /> Party Package
            </span>
            <p className="mt-6 font-display text-7xl font-bold text-blue-600 sm:text-8xl" data-testid="birthday-price">$225</p>
            <div className="mt-3 flex flex-wrap items-center justify-center gap-3">
              <span className="rounded-full bg-white px-4 py-1.5 text-sm font-extrabold text-slate-800 shadow-sm" data-testid="birthday-max-kids">Up to {SITE.party.maxKids} children</span>
              <span className="rounded-full bg-white px-4 py-1.5 text-sm font-extrabold text-slate-800 shadow-sm" data-testid="birthday-length">{SITE.party.length}</span>
            </div>
            <ul className="mx-auto mt-8 max-w-md space-y-3 text-left">
              {SITE.party.includes.map((item) => (
                <li key={item} className="flex items-center gap-3 rounded-2xl bg-white px-4 py-3 text-sm font-extrabold text-slate-700 shadow-sm sm:text-base">
                  <CircleCheck className="h-5 w-5 shrink-0 text-green-600" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </section>

      <section className="px-4 pb-20 sm:px-6" data-testid="birthday-deposit-section">
        <Reveal className="mx-auto max-w-2xl">
          <div className="rounded-3xl border-2 border-red-200 bg-red-50 p-8 sm:p-10" data-testid="birthday-deposit-box">
            <div className="flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-500">
                <AlertCircle className="h-6 w-6 text-white" aria-hidden="true" />
              </span>
              <h2 className="text-2xl font-semibold sm:text-3xl">Reserve Your Date</h2>
            </div>
            <p className="mt-5 text-base font-bold text-slate-700 md:text-lg">
              A <span className="text-red-600">$50 nonrefundable booking deposit</span> is required.
            </p>
            <div className="mt-6 grid gap-4 sm:grid-cols-3">
              <div className="rounded-2xl bg-white p-4 text-center shadow-sm">
                <p className="text-xs font-extrabold uppercase tracking-widest text-slate-500">Deposit</p>
                <p className="font-display text-3xl font-bold text-red-500" data-testid="deposit-amount">$50</p>
                <p className="mt-1 text-xs font-extrabold uppercase tracking-wide text-red-500">Nonrefundable</p>
              </div>
              <div className="rounded-2xl bg-white p-4 text-center shadow-sm">
                <p className="text-xs font-extrabold uppercase tracking-widest text-slate-500">Party total</p>
                <p className="font-display text-3xl font-bold text-blue-600">$225</p>
                <p className="mt-1 text-xs font-semibold text-slate-500">Deposit goes toward total</p>
              </div>
              <div className="rounded-2xl bg-white p-4 text-center shadow-sm">
                <p className="text-xs font-extrabold uppercase tracking-widest text-slate-500">Remaining balance</p>
                <p className="font-display text-3xl font-bold text-slate-800" data-testid="deposit-balance">$175</p>
                <p className="mt-1 text-xs font-semibold text-slate-500">Due after deposit</p>
              </div>
            </div>
            <p className="mt-6 text-sm font-semibold text-slate-600">
              Deposits and payments are handled directly with Ms. Jeanette — there is no online payment or
              booking system on this website.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="bg-blue-50 px-4 py-20 sm:px-6" data-testid="birthday-steps-section">
        <SectionHeading eyebrow="How booking works" title="Three Simple Steps" testid="birthday-steps-heading" />
        <div className="relative mx-auto max-w-3xl">
          <div aria-hidden="true" className="absolute bottom-8 left-7 top-8 border-l-4 border-dashed border-blue-300 sm:left-8" />
          <div className="space-y-8">
            {steps.map((step, i) => (
              <Reveal key={step.title} delay={i * 0.1}>
                <div className="relative flex items-start gap-5" data-testid={`booking-step-${i + 1}`}>
                  <span className="relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-blue-500 font-display text-2xl font-bold text-white shadow-lg shadow-blue-500/30 sm:h-16 sm:w-16">
                    {i + 1}
                  </span>
                  <div className="rounded-3xl bg-white p-6 shadow-lg shadow-blue-500/10">
                    <h3 className="font-display text-xl font-semibold">{step.title}</h3>
                    <p className="mt-2 text-sm font-semibold text-slate-600 sm:text-base">{step.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="mt-12 text-center">
          <CallButton testid="birthday-steps-call-btn">Call to Book Your Birthday Party</CallButton>
        </div>
      </section>

      <section className="relative overflow-hidden bg-red-500 px-4 py-20 sm:px-6" data-testid="birthday-final-cta">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <PartyPopper className="absolute left-[8%] top-[18%] h-8 w-8 text-white/25" />
          <Cake className="absolute bottom-[20%] right-[10%] h-8 w-8 text-white/25" />
        </div>
        <Reveal className="relative mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-semibold text-white sm:text-4xl lg:text-5xl">Ready to Make Their Day Extra Special?</h2>
          <p className="mx-auto mt-5 max-w-2xl text-base font-semibold text-red-100 md:text-lg">
            Give your child more than a birthday party. Give them a memory they'll talk about.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
            <a
              href={SITE.phoneHref}
              data-testid="birthday-final-call-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-extrabold text-red-600 shadow-lg shadow-black/10 transition-transform duration-200 hover:-translate-y-0.5 hover:scale-105 focus:outline-none focus:ring-4 focus:ring-yellow-300"
            >
              <Phone className="h-5 w-5" aria-hidden="true" />
              Call Ms. Jeanette — {SITE.phone}
            </a>
            <a
              href={SITE.emailHref}
              data-testid="birthday-final-email-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-yellow-400 px-7 py-3.5 text-base font-extrabold text-slate-900 shadow-lg shadow-black/10 transition-transform duration-200 hover:-translate-y-0.5 hover:scale-105 focus:outline-none focus:ring-4 focus:ring-yellow-300"
            >
              <Mail className="h-5 w-5" aria-hidden="true" />
              Email Ms. Jeanette
            </a>
          </div>
        </Reveal>
      </section>
    </>
  );
}
